import { TTypeOfCodec } from "../../shared/src/codecs/codec";
import { union } from "../../shared/src/codecs/types/union";
import { literal } from "../../shared/src/codecs/types/literal";
import { requireExhaustive } from "../../shared/src/util";
import { pipe } from "fp-ts/lib/function";
import { array, string } from "fp-ts";

export const UserCaseUserType = union([
    literal("unknown"),
    literal("client"),
    literal("authorised_third_party"),
    literal("registered_third_party"),
    literal("executor"),
    literal("deceased"),
    literal("beneficiary_receiving_payment"),
    literal("beneficiary_not_receiving_payment"),
    literal("giftor"),
    literal("company_executor"),
    literal("personal_representative"),
    literal("lpa"),
    literal("introducer"),
    literal("buyer"),
    literal("occupant"),
    literal("keyholder"),
]);
export type TUserCaseUserTypeCodec = typeof UserCaseUserType;
export type TUserCaseUserType = TTypeOfCodec<TUserCaseUserTypeCodec>;

const UserCaseUserTypeDisplayName = union([
    literal("Unknown"),
    literal("Client"),
    literal("Authorised third party"),
    literal("Registered third party"),
    literal("Executor"),
    literal("Deceased"),
    literal("Beneficiary receiving payment"),
    literal("Beneficiary not receiving payment"),
    literal("Giftor"),
    literal("Company executor"),
    literal("Personal representative"),
    literal("LPA"),
    literal("Introducer"),
    literal("Buyer"),
    literal("Occupant"),
    literal("Keyholder"),
]);
type TUserCaseUserTypeDisplayName = TTypeOfCodec<typeof UserCaseUserTypeDisplayName>;

export const UserCaseUserType_UserCaseUserTypeDisplayName = (userType: TUserCaseUserType): TUserCaseUserTypeDisplayName =>
    userType === "unknown" ? "Unknown"
    : userType === "client" ? "Client"
    : userType === "authorised_third_party" ? "Authorised third party"
    : userType === "registered_third_party" ? "Registered third party"
    : userType === "executor" ? "Executor"
    : userType === "deceased" ? "Deceased"
    : userType === "beneficiary_receiving_payment" ? "Beneficiary receiving payment"
    : userType === "beneficiary_not_receiving_payment" ? "Beneficiary not receiving payment"
    : userType === "giftor" ? "Giftor"
    : userType === "company_executor" ? "Company executor"
    : userType === "personal_representative" ? "Personal representative"
    : userType === "lpa" ? "LPA"
    : userType === "introducer" ? "Introducer"
    : userType === "buyer" ? "Buyer"
    : userType === "occupant" ? "Occupant"
    : userType === "keyholder" ? "Keyholder"
    : requireExhaustive(userType);

export const ReceivesConsumerNps: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "authorised_third_party",
    "registered_third_party",
    "personal_representative",
    "lpa",
    "company_executor",
    "beneficiary_receiving_payment",
    "beneficiary_not_receiving_payment",
];

export const ReceivesOnboardingCompleteEmail: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "personal_representative",
    "company_executor",
    "lpa",
    "giftor",
    "beneficiary_receiving_payment",
];

export const HasBankAccountAccess: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "beneficiary_receiving_payment",
    "personal_representative",
];

export const CanBeBankAccountRecipient: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "deceased",
    "beneficiary_receiving_payment",
    "personal_representative",
    "lpa",
];

export const RequiresIdCheck: Array<TUserCaseUserType> = [
    "client",
    "registered_third_party",
    "executor",
    "beneficiary_receiving_payment",
    "beneficiary_not_receiving_payment",
    "giftor",
    "personal_representative",
    "lpa",
];

export const CanRequireSourceOfWealthCheck: Array<TUserCaseUserType> = [
    "client",
    "giftor",
    "registered_third_party",
    "executor",
    "personal_representative",
    "lpa",
];

export const RequiresVideoCallVerification: Array<TUserCaseUserType> = [
    "client",
    "registered_third_party",
    "executor",
    "beneficiary_receiving_payment",
    "personal_representative",
    "lpa",
];

export const RequiresBankruptcySearchPurchaseTransferOrRemortgage: Array<TUserCaseUserType> = [
    "client",
    "registered_third_party",
    "giftor",
];

export const RequiresBankruptcySearchSale: Array<TUserCaseUserType> = [
    "deceased",
];

export const RequiresBankruptcySearch: Array<TUserCaseUserType> = pipe(
    [
        ...RequiresBankruptcySearchPurchaseTransferOrRemortgage,
        ...RequiresBankruptcySearchSale,
    ],
    array.uniq<TUserCaseUserType>(string.Eq),
);

export const CanHelpOthersWithIdCheck: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "personal_representative",
    "lpa",
];

export const CanSettlePayment: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "personal_representative",
    "executor",
];

export const CanConfirmCaseUsers: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "personal_representative",
    "lpa",
];

export const CanAddRelatedTransaction: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "personal_representative",
    "lpa",
];

export const CanAnswerPurchasePaymentDetailsQuestions: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "personal_representative",
    "lpa"
];

export const CanAnswerRemortgageMortgageDetailsQuestions: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "personal_representative",
    "lpa"
];

export const CanAnswerSDLTQuestions: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "personal_representative",
];

export const CanAnswerChargeAccountNumbersQuestions: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "personal_representative",
    "executor",
];

export const CanAnswerDivorceDocumentQuestion: Array<TUserCaseUserType> = [
    "client",
];

export const CanAgreeBSAAdvice: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "executor",
    "personal_representative",
    "lpa"
];

export const RequiresDualRepConsent: Array<TUserCaseUserType> = [
    "client",
    "personal_representative",
    "lpa",
    "executor",
];

// Who we name as "our client" in correspondence. Same membership as the TOE and dual-rep
// sets, and for the same reason: these are the people whose matter it is. A probate sale is
// executor-led, so filtering on "client" alone would leave most Sail Legal cases unnamed.
export const IsClientForCorrespondence: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "personal_representative",
    "lpa",
];

export const CanAnswerPropertyInformationForms: Array<TUserCaseUserType> = [
    "client",
    "authorised_third_party",
    "personal_representative",
    "executor",
];

export const MustSignPif: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "personal_representative",
    "lpa",
];

export const MustAgreeToTOE: Array<TUserCaseUserType> = [
    "client",
    "executor",
    "personal_representative",
    "lpa",
];

export const CanInstructCase: Array<TUserCaseUserType> = [
    "client",
    "executor",
];

export const RequiredToAnswerJointOwnershipHeldAsQuestion: Array<TUserCaseUserType> = [
    "client",
    "executor",
];

export const RequiredToAnswerDeclarationOfTrustReferralQuestion: Array<TUserCaseUserType> = [
    "client",
    "executor",
];

export const RequiredToAnswerWillReferralQuestion: Array<TUserCaseUserType> = [];

export const OwnershipStatus = union([
    literal("unknown"),
    literal("new_owner"),
    literal("remaining_owner"),
    literal("departing_owner"),
    literal("not_an_owner"),
]);
export type TOwnershipStatusCodec = typeof OwnershipStatus;
export type TOwnershipStatus = TTypeOfCodec<typeof OwnershipStatus>;

const OwnershipStatusDisplayName = union([
    literal("Unknown"),
    literal("New owner"),
    literal("Remaining owner"),
    literal("Departing owner"),
    literal("Not an owner"),
]);
type TOwnershipStatusDisplayName = TTypeOfCodec<typeof OwnershipStatusDisplayName>;

export const OwnershipStatus_DisplayName = (status: TOwnershipStatus): TOwnershipStatusDisplayName =>
    status === "unknown" ? "Unknown"
    : status === "new_owner" ? "New owner"
    : status === "remaining_owner" ? "Remaining owner"
    : status === "departing_owner" ? "Departing owner"
    : status === "not_an_owner" ? "Not an owner"
    : requireExhaustive(status);

export const OwnershipStatusesRequiringIdCheck: Array<TOwnershipStatus> = [
    "new_owner",
    "remaining_owner",
];

export const RequiredToHaveOwnershipStatus: Array<TUserCaseUserType> = [
    "client",
    "registered_third_party",
    "executor",
    "deceased",
    "personal_representative",
    "lpa",
];

export const CanAccessPortal: Array<TUserCaseUserType> = pipe(
    [
        ...HasBankAccountAccess,
        ...RequiresIdCheck,
        ...CanHelpOthersWithIdCheck,
        ...CanSettlePayment,
        ...CanConfirmCaseUsers,
        ...CanAddRelatedTransaction,
        ...CanAnswerPurchasePaymentDetailsQuestions,
        ...CanAnswerSDLTQuestions,
        ...CanAnswerChargeAccountNumbersQuestions,
        ...CanInstructCase,
        ...RequiredToAnswerJointOwnershipHeldAsQuestion,
        ...RequiredToAnswerDeclarationOfTrustReferralQuestion,
        ...RequiredToAnswerWillReferralQuestion,
    ],
    array.uniq<TUserCaseUserType>(string.Eq),
);

export const ReceivesInstructedEmail: Array<TUserCaseUserType> = pipe(
    [
        ...CanAccessPortal,
        "company_executor" as const,
        "introducer" as const,
    ],
    array.uniq<TUserCaseUserType>(string.Eq),
);

export const UserCaseUserTypePartyGroup = union([
    literal("Executors"),
    literal("Company executors"),
    literal("Personal representatives"),
    literal("Attorneys (LPA)"),
    literal("Clients"),
    literal("Buyers"),
    literal("Money in & out"),
    literal("Not acting"),
    literal("Needs a type"),
]);
export type TUserCaseUserTypePartyGroupCodec = typeof UserCaseUserTypePartyGroup;
export type TUserCaseUserTypePartyGroup = TTypeOfCodec<TUserCaseUserTypePartyGroupCodec>;

export const UserCaseUserType_PartyGroup = (userType: TUserCaseUserType): TUserCaseUserTypePartyGroup =>
    userType === "executor" ? "Executors"
    : userType === "company_executor" ? "Company executors"
    : userType === "personal_representative" ? "Personal representatives"
    : userType === "lpa" ? "Attorneys (LPA)"
    : userType === "client" ? "Clients"
    : userType === "buyer" ? "Buyers"
    : userType === "giftor" ? "Money in & out"
    : userType === "beneficiary_receiving_payment" ? "Money in & out"
    : userType === "beneficiary_not_receiving_payment" ? "Money in & out"
    : userType === "deceased" ? "Not acting"
    : userType === "authorised_third_party" ? "Not acting"
    : userType === "registered_third_party" ? "Not acting"
    : userType === "introducer" ? "Not acting"
    : userType === "occupant" ? "Not acting"
    : userType === "keyholder" ? "Not acting"
    : userType === "unknown" ? "Needs a type"
    : requireExhaustive(userType);

export const PartyGroupOrder: Array<TUserCaseUserTypePartyGroup> = UserCaseUserTypePartyGroup.values;

export const UserCaseUserTypePartyGroupTone = union([
    literal("acting"),
    literal("money"),
    literal("passive"),
    literal("needs_attention"),
]);
export type TUserCaseUserTypePartyGroupToneCodec = typeof UserCaseUserTypePartyGroupTone;
export type TUserCaseUserTypePartyGroupTone = TTypeOfCodec<TUserCaseUserTypePartyGroupToneCodec>;

export const UserCaseUserTypePartyGroup_Tone = (group: TUserCaseUserTypePartyGroup): TUserCaseUserTypePartyGroupTone =>
    group === "Executors" ? "acting"
    : group === "Company executors" ? "acting"
    : group === "Personal representatives" ? "acting"
    : group === "Attorneys (LPA)" ? "acting"
    : group === "Clients" ? "acting"
    : group === "Buyers" ? "acting"
    : group === "Money in & out" ? "money"
    : group === "Not acting" ? "passive"
    : group === "Needs a type" ? "needs_attention"
    : requireExhaustive(group);

export const CaseUserTypes: Array<TUserCaseUserType> = [
    "unknown",
    "client",
    "authorised_third_party",
    "registered_third_party",
    "executor",
    "deceased",
    "beneficiary_receiving_payment",
    "beneficiary_not_receiving_payment",
    "giftor",
    "company_executor",
    "personal_representative",
    "lpa",
    "introducer",
];

export const SellerPartyUserTypes: Array<TUserCaseUserType> = [
    "unknown",
    "client",
    "executor",
    "company_executor",
    "personal_representative",
    "lpa",
    "deceased",
    "beneficiary_receiving_payment",
    "beneficiary_not_receiving_payment",
    "authorised_third_party",
    "occupant",
    "keyholder",
    "introducer",
];

export const BuyerPartyUserTypes: Array<TUserCaseUserType> = [
    "unknown",
    "buyer",
    "authorised_third_party",
    "occupant",
    "keyholder",
    "introducer",
];
